import * as Sentry from "@sentry/react";
import mainConfig from "../config/main.config";
import { BackendCalls } from "common/types";

function resolveFetchArgs(input: RequestInfo | URL, init?: RequestInit) {
  const url = typeof input === "string" || input instanceof URL ? String(input) : input.url;
  let method = "GET";
  if (init?.method) method = init.method;
  else if (typeof input !== "string" && !(input instanceof URL) && (input as Request).method) {
    method = (input as Request).method;
  }
  return { url, method: method.toUpperCase() };
}

const makeBaseReg = (url?: string) => url && new RegExp(`^${url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\/?$/, "/")}`);

class SentryHelper {
  private shouldAlert(backend: BackendCalls) {
    return backend === "registration" || backend === "ecommerce" || backend === "cms" || backend === "ringbox";
  }

  private envConfig() {
    return mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT);
  }

  private getTracePropagationTargets(): (string | RegExp)[] {
    const cfg = this.envConfig();
    return ["localhost", makeBaseReg(cfg?.registrationApiUrl), makeBaseReg(cfg?.baseApiUrl)].filter(Boolean) as (string | RegExp)[];
  }

  getNetworkDetailAllowUrls() {
    const cfg = this.envConfig();
    if (!cfg) return [];
    const u = (s: string | undefined) => (s ? s.replace(/\/?$/, "/") : undefined);
    return [u(cfg.registrationApiUrl), u(cfg.baseApiUrl), u(cfg.baseMagentoApiUrl)].filter(Boolean) as string[];
  }

  private hostOf(url?: string) {
    if (!url) return "";
    try {
      return new URL(url).host;
    } catch {
      return "";
    }
  }

  private pathOf(url?: string) {
    if (!url) return "";
    try {
      return new URL(url).pathname || "/";
    } catch {
      return "";
    }
  }

  private normalizeStarts(u: string, base?: string) {
    if (!base) return false;
    return u.startsWith(base.toLowerCase().replace(/\/?$/, "/"));
  }

  private getBackendSystem(rawUrl: string): BackendCalls {
    const cfg = this.envConfig();
    const urlStr = String(rawUrl);
    const u = urlStr.toLowerCase();
    const ECOM_PATH_RE = /(\/graphql|\/rest\/|\/cart|\/checkout|\/catalog)/i;

    const regBase = cfg?.registrationApiUrl?.toLowerCase();
    const apiBase = cfg?.baseApiUrl?.toLowerCase();
    const magentoBase = cfg?.baseMagentoApiUrl?.toLowerCase();

    if (this.normalizeStarts(u, regBase)) return "registration";

    if (this.normalizeStarts(u, apiBase)) {
      return "ecommerce";
    }

    if (this.normalizeStarts(u, magentoBase)) {
      const p = this.pathOf(urlStr).toLowerCase();
      if (p.startsWith("/media") || p.startsWith("/static")) return "cms";
      if (ECOM_PATH_RE.test(p)) return "ecommerce";
      return "ecommerce";
    }

    try {
      const { host, pathname } = new URL(urlStr);
      const h = host.toLowerCase();
      const p = pathname.toLowerCase();

      if (
        /(^|\.)cms[.-]/.test(h) ||
        h.includes("contentful") ||
        h.includes("cloudfront") ||
        h.includes("fastly") ||
        /\/(cms|content|assets|media|images?)\//.test(p)
      ) {
        return "cms";
      }

      if (/(^|\.)ringbox[.-]/.test(h) || /\/ringbox\//.test(p)) {
        return "ringbox";
      }
    } catch {}

    if (u.includes("registration")) return "registration";
    if (u.includes("ringbox")) return "ringbox";
    if (u.includes("magento") || u.includes("/graphql") || u.includes("/rest/")) return "ecommerce";
    if (u.includes("/media/") || u.includes("/static/") || u.includes("/assets/")) return "cms";

    return "unknown";
  }

  private captureBackendFailure(method: string, url: string, status: number, statusText?: string, correlationId?: string | null) {
    const backend = this.getBackendSystem(url);
    if (!this.shouldAlert(backend)) return;

    const host = this.hostOf(url);

    Sentry.captureMessage(`Backend ${backend} returned ${status} ${statusText ?? ""}`.trim(), {
      level: "error",
      tags: {
        backend_system: backend,
        http_status: String(status),
        http_method: method.toUpperCase(),
        endpoint_host: host,
        ...(correlationId ? { correlation_id: correlationId } : {})
      },
      extra: { url }
    });
  }

  private installFetchMonitor() {
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;

    const origFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const { url, method } = resolveFetchArgs(input, init);
      
      // Extract correlationId from request headers
      let requestCorrelationId: string | null = null;
      if (init?.headers) {
        if (init.headers instanceof Headers) {
          requestCorrelationId = init.headers.get("x-correlationid") || init.headers.get("X-CorrelationId");
        } else if (Array.isArray(init.headers)) {
          const corrHeader = init.headers.find(([key]) => key.toLowerCase() === "x-correlationid");
          if (corrHeader) requestCorrelationId = corrHeader[1];
        } else {
          // Plain object headers
          const headers = init.headers as Record<string, string>;
          requestCorrelationId = headers["x-correlationid"] || headers["X-CorrelationId"] || headers["X-Correlationid"] || null;
        }
      }

      try {
        const resp = await origFetch(input, init);
        if (resp.status >= 500) {
          const correlationId = resp.headers.get("x-correlationid") || requestCorrelationId;
          this.captureBackendFailure(method, url, resp.status, resp.statusText, correlationId);
        }
        return resp;
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            backend_system: this.getBackendSystem(url),
            http_method: method,
            endpoint_host: this.hostOf(url),
            network_phase: "fetch_throw",
            ...(requestCorrelationId ? { correlation_id: requestCorrelationId } : {})
          },
          extra: { url }
        });
        throw err;
      }
    };
  }

  private installXHRMonitor() {
    if (typeof window === "undefined" || typeof window.XMLHttpRequest === "undefined") return;

    const OrigXHR = window.XMLHttpRequest;
    const self = this;

    class XHRProxy extends OrigXHR {
      private __url = "";
      private __method = "GET";
      private __correlationId: string | null = null;

      open(method: string, url: string, async?: boolean, username?: string | null, password?: string | null) {
        this.__method = method || "GET";
        this.__url = url;
        return super.open(method, url, async ?? true, username ?? null, password ?? null);
      }

      setRequestHeader(name: string, value: string) {
        // Capture correlationId from request headers
        if (name.toLowerCase() === "x-correlationid") {
          this.__correlationId = value;
        }
        return super.setRequestHeader(name, value);
      }

      send(body?: Document | BodyInit | null) {
        this.addEventListener("loadend", () => {
          try {
            const status = (this.status as number) || 0;
            if (status >= 500) {
              const correlationId = this.getResponseHeader("x-correlationid") || this.__correlationId;
              self.captureBackendFailure(this.__method, this.__url, status, this.statusText, correlationId);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Sentry XHR monitor error:", e);
          }
        });

        this.addEventListener("error", () => {
          try {
            Sentry.captureMessage("Network error during XHR request", {
              level: "error",
              tags: {
                backend_system: self.getBackendSystem(this.__url),
                http_method: this.__method,
                endpoint_host: self.hostOf(this.__url),
                network_phase: "xhr_error",
                ...(this.__correlationId ? { correlation_id: this.__correlationId } : {})
              },
              extra: { url: this.__url }
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Sentry XHR monitor error:", e);
          }
        });

        this.addEventListener("timeout", () => {
          try {
            Sentry.captureMessage("XHR request timed out", {
              level: "error",
              tags: {
                backend_system: self.getBackendSystem(this.__url),
                http_method: this.__method,
                endpoint_host: self.hostOf(this.__url),
                network_phase: "xhr_timeout",
                ...(this.__correlationId ? { correlation_id: this.__correlationId } : {})
              },
              extra: { url: this.__url, timeout_ms: (this.timeout as number) || undefined }
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Sentry XHR monitor error:", e);
          }
        });

        return super.send(body as any);
      }
    }

    window.XMLHttpRequest = XHRProxy;
  }

  private shouldShowFeedbackWidget() {
    // Hide feedback widget on Freya routes

    const pathname = window.location.pathname;
    const isFreyaRoute = pathname.startsWith("/freya") || pathname.endsWith("/freya") || pathname.includes("/freya/");
    // eslint-disable-next-line no-console
    console.log(`Sentry: Feedback widget check - pathname: ${pathname}, isFreyaRoute: ${isFreyaRoute}, shouldShow: ${!isFreyaRoute}`);
    return !isFreyaRoute;
  }

  private getFeedbackIntegration() {
    // Only auto-inject if NOT on a Freya route
    const shouldShow = this.shouldShowFeedbackWidget();

    // eslint-disable-next-line no-console
    console.log(`Sentry: Creating feedback integration with autoInject: ${shouldShow}`);

    return Sentry.feedbackIntegration({
      // Conditionally inject based on current route
      autoInject: shouldShow,
      showBranding: false,
      colorScheme: "light",
      isNameRequired: true,
      isEmailRequired: true,
      themeLight: {
        submitBackground: "#3054A1",
        submitBackgroundHover: "#25417C",
        submitBorder: "#3054A1"
      }
    });
  }

  init() {
    const env = process.env.REACT_APP_ENVIRONMENT;
    const enabledEnvs = ["prod", "preprod"];
    const isLocalEnv = env === "local" || env === "localAgainstShow" || env === "localAgainstDev";

    // For local environments, only enable feedback widget for testing
    if (isLocalEnv) {
      // eslint-disable-next-line no-console
      console.log(`Sentry: Local mode - only feedback widget enabled for testing`);
      // eslint-disable-next-line no-console
      console.log(`Sentry DSN: ${process.env.REACT_APP_DSN ? "Set" : "Not set"}`);

      Sentry.init({
        dsn: process.env.REACT_APP_DSN,
        environment: env,
        release: process.env.REACT_APP_RELEASE_VERSION || `mercury-odin@${process.env.REACT_APP_VERSION || "unknown"}`,
        // Disable all monitoring features in local
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        beforeSend() {
          // Don't send any errors to Sentry in local mode
          return null;
        },
        integrations: [this.getFeedbackIntegration()]
      });

      // eslint-disable-next-line no-console
      console.log("Sentry initialized with feedback integration");

      return;
    }

    // Enable full Sentry for prod/preprod environments
    if (!enabledEnvs.includes(env as string)) {
      // eslint-disable-next-line no-console
      console.log(`Sentry disabled - environment '${env}' not in [${enabledEnvs.join(", ")}]`);
      return;
    }

    // eslint-disable-next-line no-console
    console.log(`Sentry: Initializing for ${env} environment`);

    const isProd = env === "prod";
    const isPreprod = env === "preprod";
    const allowUrls = this.getNetworkDetailAllowUrls();
    const traceTargets = this.getTracePropagationTargets();

    const integrations = [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        networkDetailAllowUrls: allowUrls,
        networkRequestHeaders: ["X-correlationid"],
        maskAllText: true,
        maskAllInputs: true,
        block: ["[data-sentry-block]"],
        mask: ["[data-sentry-mask]", ".customer-details", '[type="password"]'],
        unmask: ["[data-sentry-unmask]", ".replay-unmask"]
      })
    ];

    // Only add feedback integration for preprod (not prod, not show)
    if (isPreprod) {
      integrations.push(this.getFeedbackIntegration());
      // eslint-disable-next-line no-console
      console.log("Sentry: Feedback integration enabled for preprod");
    }

    Sentry.init({
      dsn: process.env.REACT_APP_DSN,
      // Set release version for source map matching
      release: process.env.REACT_APP_RELEASE_VERSION || `mercury-odin@${process.env.REACT_APP_VERSION || "unknown"}`,
      // Filter out raw Event objects that shouldn't be reported
      beforeSend(event, hint) {
        // If the original exception is a raw DOM Event object, drop it
        const originalException = hint.originalException;
        if (
          originalException &&
          typeof originalException === "object" &&
          (originalException instanceof Event || originalException.constructor?.name === "Event")
        ) {
          // eslint-disable-next-line no-console
          console.warn("Sentry: Dropping raw Event object from reporting", originalException);
          return null; // Drop the event
        }

        // Filter out network errors for external resources (images, logos, etc.)
        // These are expected failures from external sites we don't control
        if (event.message && event.message.includes("Network error during XHR request")) {
          const url = event.extra?.url as string;
          // Only drop errors for external domains we don't control
          if (url) {
            try {
              const urlObj = new URL(url);
              const hostname = urlObj.hostname;
              if (
                !hostname.endsWith('rxglobal.com') &&
                !hostname.includes('rxweb')
              ) {
                // eslint-disable-next-line no-console
                console.warn("Sentry: Dropping external network error:", url);
                return null;
              }
            } catch (e) {
              // If the URL is invalid, keep the error (do not drop)
              // eslint-disable-next-line no-console
              console.warn("Sentry: Failed to parse URL, keeping error:", url, e);
            }
          }
        }

        // Filter out JSPrintManager WebSocket errors - these are expected when print server isn't available
        if (event.message && (
          event.message.includes("JSPrintManager") || 
          event.message.includes("Print server") ||
          event.message.includes("WebSocket")
        )) {
          // eslint-disable-next-line no-console
          console.warn("Sentry: Dropping JSPrintManager/WebSocket error");
          return null;
        }

        return event;
      },
      integrations,
      environment: env,
      // Performance Monitoring (allow tuning per environment if desired later)
      tracesSampleRate: isProd ? 0.05 : 0,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: traceTargets,
      // Set profilesSampleRate to 1.0 to profile every transaction.
      // Since profilesSampleRate is relative to tracesSampleRate,
      // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
      // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
      // results in 25% of transactions being profiled (0.5*0.5=0.25)
      profilesSampleRate: isProd ? 1.0 : 0.5,
      // Session Replay
      replaysSessionSampleRate: isProd ? 0.1 : 0.2,
      replaysOnErrorSampleRate: 1.0
    });

    this.installFetchMonitor();
    this.installXHRMonitor();
  }
}

const sentryHelper = new SentryHelper();
export default sentryHelper;
